import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
    AlertTriangle,
    CheckCircle2,
    ChevronDown,
    ChevronRight,
    ClipboardList,
    FileText,
    History,
    Plus,
    Printer,
    RefreshCw,
    Save,
    Search,
    Tag,
    Trash2,
    Truck,
    X,
    XCircle,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import { apiFetch } from '../utils/api';
import { checkPermission } from '../utils/userRoles';
import { printSolicitudCompra } from '../utils/printSolicitudCompra';

const ESTADOS = [
    { value: 'todas', label: 'Todas' },
    { value: 'borrador', label: 'Borradores' },
    { value: 'lista', label: 'Listas' },
    { value: 'recibida', label: 'Recibidas' },
    { value: 'anulada', label: 'Anuladas' },
];

const ESTADO_LABEL = {
    borrador: 'Borrador',
    lista: 'Lista',
    recibida: 'Recibida',
    anulada: 'Anulada',
};

const ESTADO_CLASS = {
    borrador: 'bg-amber-100 text-amber-800',
    lista: 'bg-indigo-100 text-indigo-800',
    recibida: 'bg-emerald-100 text-emerald-800',
    anulada: 'bg-gray-200 text-gray-600',
};

const CLP = new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
});

const QTY = new Intl.NumberFormat('es-CL', { maximumFractionDigits: 3 });

const fmt = (value) => CLP.format(Math.round(Number(value) || 0));
const fmtQty = (value, ventaPorPeso) => `${QTY.format(Number(value) || 0)}${ventaPorPeso ? ' kg' : ''}`;

const toNumber = (value) => {
    if (typeof value === 'string') value = value.trim().replace(',', '.');
    const n = Number(value);
    return Number.isFinite(n) ? n : NaN;
};

const todayISO = () => {
    const now = new Date();
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
    return local.toISOString().slice(0, 10);
};

const fmtDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('es-CL', { timeZone: 'America/Santiago' });
};

const fmtDateTime = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('es-CL', { timeZone: 'America/Santiago' });
};

const lineKey = (proveedorId, productoId) => `${proveedorId}:${productoId}`;

const totalLinea = (linea) => {
    const cantidad = Number(linea.cantidad_pedida ?? linea.cantidad_recibida) || 0;
    const precio = Number(linea.precio_unitario) || 0;
    return linea.total_linea != null ? Number(linea.total_linea) : cantidad * precio;
};

export default function SolicitudesCompraPage() {
    const { user } = useAuth();
    const canEdit = Number(user?.role) === 4
        || !Array.isArray(user?.permissions)
        || checkPermission(user, 'compras_editar');
    const canView = Number(user?.role) === 4
        || !Array.isArray(user?.permissions)
        || checkPermission(user, 'compras_ver');

    const [tab, setTab] = useState('armar');
    const [message, setMessage] = useState(null);

    const notify = useCallback((type, text) => setMessage({ type, text }), []);

    useEffect(() => {
        if (!message) return undefined;
        const t = setTimeout(() => setMessage(null), 7000);
        return () => clearTimeout(t);
    }, [message]);

    const api = useCallback(async (path, options = {}) => {
        const res = await apiFetch(`${API_URL}${path}`, options);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            const error = new Error(data.error || `Error ${res.status}`);
            error.payload = data;
            throw error;
        }
        return data;
    }, []);

    // ---------------------------------------------------------------- Armar
    const [sugerido, setSugerido] = useState(null);
    const [loadingSugerido, setLoadingSugerido] = useState(false);
    const [selected, setSelected] = useState({});
    const [cantidades, setCantidades] = useState({});
    const [expandedOffers, setExpandedOffers] = useState({});
    const [savingDraft, setSavingDraft] = useState(false);

    const loadSugerido = useCallback(async () => {
        setLoadingSugerido(true);
        try {
            const data = await api('/api/solicitudes-compra/sugerido');
            setSugerido(data);
            const nextSelected = {};
            const nextCantidades = {};
            for (const grupo of data.grupos || []) {
                for (const linea of grupo.lineas || []) {
                    const key = lineKey(grupo.proveedor.proveedor_id, linea.producto_id);
                    const inicial = linea.en_borrador && linea.cantidad_borrador != null
                        ? linea.cantidad_borrador
                        : linea.cantidad_sugerida;
                    nextCantidades[key] = String(inicial ?? '');
                    nextSelected[key] = Number(inicial) > 0;
                }
            }
            setSelected(nextSelected);
            setCantidades(nextCantidades);
        } catch (err) {
            notify('error', err.message);
            setSugerido({ grupos: [], sin_ofertas: [], en_lista: [], generado_at: null });
        } finally {
            setLoadingSugerido(false);
        }
    }, [api, notify]);

    const allKeys = useMemo(() => (
        (sugerido?.grupos || []).flatMap((grupo) => (
            (grupo.lineas || []).map((linea) => lineKey(grupo.proveedor.proveedor_id, linea.producto_id))
        ))
    ), [sugerido]);

    const selectedCount = allKeys.filter((key) => selected[key]).length;

    const toggleAll = () => {
        const target = !(allKeys.length > 0 && allKeys.every((key) => selected[key]));
        setSelected((prev) => {
            const next = { ...prev };
            allKeys.forEach((key) => { next[key] = target; });
            return next;
        });
    };

    const toggleGroup = (grupo) => {
        const keys = (grupo.lineas || []).map((linea) => lineKey(grupo.proveedor.proveedor_id, linea.producto_id));
        const target = !keys.every((key) => selected[key]);
        setSelected((prev) => {
            const next = { ...prev };
            keys.forEach((key) => { next[key] = target; });
            return next;
        });
    };

    const updateCantidad = (key, value) => {
        setCantidades((prev) => ({ ...prev, [key]: value }));
        if (toNumber(value) > 0) {
            setSelected((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
        }
    };

    const guardarBorrador = async () => {
        if (!canEdit) return;
        const items = [];
        for (const grupo of sugerido?.grupos || []) {
            for (const linea of grupo.lineas || []) {
                const key = lineKey(grupo.proveedor.proveedor_id, linea.producto_id);
                if (!selected[key]) continue;
                const cantidad = toNumber(cantidades[key]);
                if (!Number.isFinite(cantidad) || cantidad < 0) {
                    notify('error', `${linea.nombre}: cantidad inválida`);
                    return;
                }
                items.push({ producto_id: linea.producto_id, cantidad_pedida: cantidad });
            }
        }
        if (items.length === 0) {
            notify('error', 'Seleccione al menos un producto y escriba la cantidad a pedir');
            return;
        }

        setSavingDraft(true);
        try {
            const data = await api('/api/solicitudes-compra/generar', {
                method: 'POST',
                body: JSON.stringify({ items }),
            });
            const omitidas = Array.isArray(data.omitidas) ? data.omitidas.length : 0;
            notify(
                omitidas ? 'warning' : 'success',
                `${data.lineas_guardadas} línea(s) guardada(s) en ${data.solicitudes?.length || 0} borrador(es).`
                + (omitidas ? ` ${omitidas} producto(s) se omitieron.` : '')
            );
            setTab('solicitudes');
            await Promise.all([loadSugerido(), loadSolicitudes()]);
        } catch (err) {
            notify('error', err.message);
        } finally {
            setSavingDraft(false);
        }
    };

    // --------------------------------------------------------- Solicitudes
    const [solicitudes, setSolicitudes] = useState([]);
    const [estadoFiltro, setEstadoFiltro] = useState('todas');
    const [loadingSolicitudes, setLoadingSolicitudes] = useState(false);
    const [detail, setDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [savingDetail, setSavingDetail] = useState(false);

    const loadSolicitudes = useCallback(async (estado = estadoFiltro) => {
        setLoadingSolicitudes(true);
        try {
            const query = estado && estado !== 'todas' ? `?estado=${encodeURIComponent(estado)}` : '';
            const data = await api(`/api/solicitudes-compra${query}`);
            setSolicitudes(Array.isArray(data) ? data : []);
        } catch (err) {
            notify('error', err.message);
            setSolicitudes([]);
        } finally {
            setLoadingSolicitudes(false);
        }
    }, [api, estadoFiltro, notify]);

    useEffect(() => {
        loadSolicitudes(estadoFiltro);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [estadoFiltro]);

    const abrirSolicitud = async (id) => {
        setLoadingDetail(true);
        try {
            const data = await api(`/api/solicitudes-compra/${id}`);
            setDetail(data);
        } catch (err) {
            notify('error', err.message);
        } finally {
            setLoadingDetail(false);
        }
    };

    const actualizarLineaDetalle = (lineaId, field, value) => {
        setDetail((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                lineas: prev.lineas.map((linea) => (
                    linea.id === lineaId ? { ...linea, [field]: value } : linea
                )),
            };
        });
    };

    const eliminarLineaDetalle = (lineaId) => {
        setDetail((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                lineas: prev.lineas.map((linea) => (
                    linea.id === lineaId ? { ...linea, _removed: !linea._removed } : linea
                )),
            };
        });
    };

    const guardarDetalle = async ({ silent = false } = {}) => {
        if (!detail) return null;
        const activas = detail.lineas.filter((linea) => !linea._removed);
        const lineas = [];
        for (const linea of activas) {
            const cantidad = toNumber(linea.cantidad_pedida);
            if (!Number.isFinite(cantidad) || cantidad < 0) {
                notify('error', `${linea.producto_nombre}: cantidad inválida`);
                return null;
            }
            lineas.push({ id: linea.id, cantidad_pedida: cantidad });
        }

        setSavingDetail(true);
        try {
            const data = await api(`/api/solicitudes-compra/${detail.id}`, {
                method: 'PUT',
                body: JSON.stringify({
                    lineas,
                    eliminar: detail.lineas.filter((linea) => linea._removed).map((linea) => linea.id),
                    observaciones: detail.observaciones || null,
                }),
            });
            setDetail(data);
            if (!silent) notify('success', 'Borrador actualizado');
            await Promise.all([loadSolicitudes(), loadSugerido()]);
            return data;
        } catch (err) {
            notify('error', err.message);
            return null;
        } finally {
            setSavingDetail(false);
        }
    };

    const dejarLista = async () => {
        if (!detail) return;
        const activas = detail.lineas.filter((linea) => !linea._removed);
        if (activas.length === 0) {
            notify('error', 'El borrador no tiene líneas');
            return;
        }
        const sinCantidad = activas.filter((linea) => !(toNumber(linea.cantidad_pedida) > 0));
        if (sinCantidad.length > 0) {
            notify('error', `Escriba la cantidad a pedir en: ${sinCantidad.map((linea) => linea.producto_nombre).join(', ')}`);
            return;
        }
        if (!window.confirm('¿Dejar esta solicitud lista para imprimir y enviar?')) return;

        const guardada = await guardarDetalle({ silent: true });
        if (!guardada) return;
        try {
            const data = await api(`/api/solicitudes-compra/${guardada.id}/lista`, { method: 'POST' });
            setDetail(data);
            notify('success', 'Solicitud lista. Ya se puede imprimir y enviar.');
            await Promise.all([loadSolicitudes(), loadSugerido()]);
        } catch (err) {
            notify('error', err.message);
        }
    };

    const anularSolicitud = async (id) => {
        if (!window.confirm('¿Anular esta solicitud? No se moverá stock.')) return;
        try {
            await api(`/api/solicitudes-compra/${id}/anular`, { method: 'POST' });
            notify('success', 'Solicitud anulada');
            if (detail?.id === id) setDetail(null);
            await Promise.all([loadSolicitudes(), loadSugerido()]);
        } catch (err) {
            notify('error', err.message);
        }
    };

    const imprimirSolicitud = async (id) => {
        try {
            const data = await api(`/api/solicitudes-compra/${id}`);
            if (!printSolicitudCompra(data)) {
                notify('warning', 'Permita las ventanas emergentes para imprimir la solicitud');
            }
        } catch (err) {
            notify('error', err.message);
        }
    };

    const imprimirDetalle = () => {
        if (!detail) return;
        if (!printSolicitudCompra(detail)) {
            notify('warning', 'Permita las ventanas emergentes para imprimir la solicitud');
        }
    };

    // ---------------------------------------------------------- Recepción
    const [bodegas, setBodegas] = useState([]);
    const [reception, setReception] = useState(null);
    const [receptionForm, setReceptionForm] = useState({
        bodega_id: '',
        numero_factura: '',
        fecha_compra: todayISO(),
        ila: '',
        flete: '',
    });
    const [receptionLineas, setReceptionLineas] = useState([]);
    const [receiving, setReceiving] = useState(false);

    const loadBodegas = useCallback(async () => {
        try {
            const data = await api('/api/solicitudes-compra/bodegas');
            setBodegas(Array.isArray(data) ? data : []);
        } catch (err) {
            notify('error', err.message);
        }
    }, [api, notify]);

    useEffect(() => { loadBodegas(); }, [loadBodegas]);

    const abrirRecepcion = async (id) => {
        try {
            const data = await api(`/api/solicitudes-compra/${id}`);
            if (data.estado !== 'lista') {
                notify('error', 'Solo se puede recepcionar una solicitud lista');
                return;
            }
            setDetail(null);
            setReception(data);
            setReceptionForm({
                bodega_id: bodegas[0]?.bodega_id ? String(bodegas[0].bodega_id) : '',
                numero_factura: '',
                fecha_compra: todayISO(),
                ila: '',
                flete: '',
            });
            setReceptionLineas((data.lineas || []).map((linea) => ({
                ...linea,
                cantidad_recibida: String(linea.cantidad_pedida ?? ''),
                precio_unitario: String(linea.precio_unitario ?? ''),
                fecha_expiracion: '',
                requiere_expiracion: false,
            })));
        } catch (err) {
            notify('error', err.message);
        }
    };

    const actualizarRecepcionLinea = (lineaId, field, value) => {
        setReceptionLineas((prev) => prev.map((linea) => (
            linea.id === lineaId ? { ...linea, [field]: value } : linea
        )));
    };

    const recepcionTotal = useMemo(() => {
        const lineas = receptionLineas.reduce((acc, linea) => (
            acc + Math.round((Number(toNumber(linea.cantidad_recibida)) || 0) * (Number(toNumber(linea.precio_unitario)) || 0))
        ), 0);
        return lineas + Math.round(Number(toNumber(receptionForm.ila)) || 0) + Math.round(Number(toNumber(receptionForm.flete)) || 0);
    }, [receptionLineas, receptionForm.ila, receptionForm.flete]);

    const recepcionar = async () => {
        if (!reception) return;
        if (!receptionForm.bodega_id) {
            notify('error', 'Seleccione la bodega de ingreso');
            return;
        }
        const items = [];
        for (const linea of receptionLineas) {
            const cantidad = toNumber(linea.cantidad_recibida);
            if (!Number.isFinite(cantidad) || cantidad < 0) {
                notify('error', `${linea.producto_nombre}: cantidad recibida inválida`);
                return;
            }
            if (cantidad === 0) continue;
            const precio = toNumber(linea.precio_unitario);
            if (!Number.isFinite(precio) || precio < 0) {
                notify('error', `${linea.producto_nombre}: precio inválido`);
                return;
            }
            if (linea.requiere_expiracion && !linea.fecha_expiracion) {
                notify('error', `${linea.producto_nombre}: indique la fecha de expiración o desmarque el requerimiento`);
                return;
            }
            items.push({
                detalle_id: linea.id,
                cantidad_recibida: cantidad,
                precio_unitario: precio,
                lineTotal: Math.round(cantidad * precio),
                fecha_expiracion: linea.requiere_expiracion ? linea.fecha_expiracion : null,
                requiere_expiracion: Boolean(linea.requiere_expiracion),
            });
        }
        if (items.length === 0) {
            notify('error', 'Ingrese al menos una cantidad recibida mayor a 0');
            return;
        }

        setReceiving(true);
        try {
            const data = await api(`/api/solicitudes-compra/${reception.id}/recepcion`, {
                method: 'POST',
                body: JSON.stringify({
                    bodega_id: Number(receptionForm.bodega_id),
                    numero_factura: receptionForm.numero_factura || null,
                    fecha_compra: receptionForm.fecha_compra || todayISO(),
                    items,
                    precio_tipo: 'bruto',
                    ila: Math.round(Number(toNumber(receptionForm.ila)) || 0),
                    flete: Math.round(Number(toNumber(receptionForm.flete)) || 0),
                    total: recepcionTotal,
                }),
            });
            const pendientes = Array.isArray(data.precios_pendientes) ? data.precios_pendientes.length : 0;
            notify(
                pendientes ? 'warning' : 'success',
                `Solicitud recepcionada y stock actualizado.`
                + (pendientes ? ` ${pendientes} precio(s) quedaron pendientes de autorización.` : '')
            );
            setReception(null);
            await Promise.all([loadSolicitudes(), loadSugerido()]);
        } catch (err) {
            notify('error', err.message);
        } finally {
            setReceiving(false);
        }
    };

    // ------------------------------------------------------------- Ofertas
    const [proveedores, setProveedores] = useState([]);
    const [offerProduct, setOfferProduct] = useState(null);
    const [offers, setOffers] = useState([]);
    const [loadingOffers, setLoadingOffers] = useState(false);
    const [offerForm, setOfferForm] = useState({
        proveedor_id: '',
        precio: '',
        codigo_proveedor: '',
        fecha_precio: todayISO(),
        es_preferido: false,
    });
    const [savingOffer, setSavingOffer] = useState(false);
    const [productSearch, setProductSearch] = useState('');
    const [productResults, setProductResults] = useState([]);
    const [searchingProducts, setSearchingProducts] = useState(false);
    const [pendientes, setPendientes] = useState({ total: 0, productos: [] });
    const [pendientesSearch, setPendientesSearch] = useState('');

    const loadProveedores = useCallback(async () => {
        try {
            const data = await api('/api/ofertas/proveedores');
            setProveedores(Array.isArray(data) ? data : []);
        } catch (err) {
            notify('error', err.message);
        }
    }, [api, notify]);

    const loadPendientes = useCallback(async (search = '') => {
        try {
            const query = search ? `?search=${encodeURIComponent(search)}` : '';
            const data = await api(`/api/ofertas/pendientes${query}`);
            setPendientes({
                total: Number(data.total) || 0,
                productos: Array.isArray(data.productos) ? data.productos : [],
            });
        } catch (err) {
            notify('error', err.message);
            setPendientes({ total: 0, productos: [] });
        }
    }, [api, notify]);

    useEffect(() => {
        loadProveedores();
        loadSugerido();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const t = setTimeout(() => loadPendientes(pendientesSearch.trim()), 350);
        return () => clearTimeout(t);
    }, [pendientesSearch, loadPendientes]);

    useEffect(() => {
        const q = productSearch.trim();
        if (q.length < 2) {
            setProductResults([]);
            setSearchingProducts(false);
            return undefined;
        }
        setSearchingProducts(true);
        const t = setTimeout(async () => {
            try {
                const data = await api(`/api/ofertas/productos?search=${encodeURIComponent(q)}`);
                setProductResults(Array.isArray(data) ? data : []);
            } catch (err) {
                notify('error', err.message);
                setProductResults([]);
            } finally {
                setSearchingProducts(false);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [productSearch, api, notify]);

    const loadOffers = useCallback(async (productoId) => {
        if (!productoId) return;
        setLoadingOffers(true);
        try {
            const data = await api(`/api/ofertas?producto_id=${productoId}`);
            setOffers(Array.isArray(data) ? data : []);
        } catch (err) {
            notify('error', err.message);
            setOffers([]);
        } finally {
            setLoadingOffers(false);
        }
    }, [api, notify]);

    const abrirOfertas = (product) => {
        if (!product) return;
        const producto = {
            id: product.id || product.producto_id,
            nombre: product.nombre,
            codigo_barra: product.codigo_barra,
            stock_total: product.stock_total,
            stock_minimo: product.stock_minimo,
        };
        setOfferProduct(producto);
        setOfferForm({
            proveedor_id: '',
            precio: '',
            codigo_proveedor: '',
            fecha_precio: todayISO(),
            es_preferido: false,
        });
        loadOffers(producto.id);
        setTab('ofertas');
    };

    const editarOferta = (oferta) => {
        setOfferForm({
            proveedor_id: String(oferta.proveedor_id),
            precio: String(oferta.precio ?? ''),
            codigo_proveedor: oferta.codigo_proveedor || '',
            fecha_precio: oferta.fecha_precio || todayISO(),
            es_preferido: Boolean(oferta.es_preferido),
        });
    };

    const guardarOferta = async (e) => {
        e.preventDefault();
        if (!offerProduct || !canEdit) return;
        if (!offerForm.proveedor_id) {
            notify('error', 'Seleccione un proveedor');
            return;
        }
        const precio = toNumber(offerForm.precio);
        if (!Number.isFinite(precio) || precio <= 0) {
            notify('error', 'Ingrese un precio de compra mayor a 0');
            return;
        }

        setSavingOffer(true);
        try {
            await api(`/api/ofertas/${offerProduct.id}`, {
                method: 'PUT',
                body: JSON.stringify({
                    proveedor_id: Number(offerForm.proveedor_id),
                    precio,
                    codigo_proveedor: offerForm.codigo_proveedor || null,
                    fecha_precio: offerForm.fecha_precio || null,
                    es_preferido: Boolean(offerForm.es_preferido),
                }),
            });
            notify('success', 'Oferta guardada');
            await Promise.all([
                loadOffers(offerProduct.id),
                loadPendientes(pendientesSearch.trim()),
                loadSugerido(),
            ]);
        } catch (err) {
            notify('error', err.message);
        } finally {
            setSavingOffer(false);
        }
    };

    const eliminarOferta = async (oferta) => {
        if (!offerProduct || !canEdit) return;
        if (!window.confirm(`¿Quitar la oferta de ${oferta.proveedor_nombre}?`)) return;
        try {
            await api(`/api/ofertas/${offerProduct.id}/${oferta.proveedor_id}`, { method: 'DELETE' });
            notify('success', 'Oferta eliminada');
            await Promise.all([
                loadOffers(offerProduct.id),
                loadPendientes(pendientesSearch.trim()),
                loadSugerido(),
            ]);
        } catch (err) {
            notify('error', err.message);
        }
    };

    // --------------------------------------------------------------- Render
    if (!canView) {
        return (
            <div className="min-h-screen bg-gray-50 p-6 md:p-8">
                <div className="max-w-3xl mx-auto bg-white rounded-2xl border border-gray-200 p-8 text-center">
                    <AlertTriangle className="mx-auto text-amber-500 mb-3" size={32} />
                    <h1 className="text-xl font-bold text-gray-800">Sin permiso para ver compras</h1>
                    <p className="text-sm text-gray-500 mt-2">Pida al administrador el permiso <code>compras_ver</code>.</p>
                </div>
            </div>
        );
    }

    const grupos = sugerido?.grupos || [];
    const sinOfertas = sugerido?.sin_ofertas || [];
    const enLista = sugerido?.en_lista || [];

    return (
        <div className="min-h-screen bg-gray-50 p-6 md:p-8">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800 tracking-tight">Solicitudes de compra</h1>
                        <p className="text-sm text-gray-500 mt-1">
                            Pedido por proveedor según stock mínimo. La comparación de precios es interna: al proveedor se le imprime solo su pedido.
                        </p>
                    </div>

                    <div className="bg-white p-1 rounded-lg shadow-sm border border-gray-200 inline-flex flex-wrap">
                        {[
                            { value: 'armar', label: 'Armar pedido', icon: ClipboardList },
                            { value: 'solicitudes', label: 'Solicitudes', icon: History },
                            { value: 'ofertas', label: 'Ofertas', icon: Tag },
                        ].map((item) => {
                            const Icon = item.icon;
                            return (
                                <button
                                    key={item.value}
                                    type="button"
                                    onClick={() => setTab(item.value)}
                                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all inline-flex items-center gap-2 ${
                                        tab === item.value
                                            ? 'bg-indigo-500 text-white shadow-sm'
                                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                    }`}
                                >
                                    <Icon size={16} />
                                    {item.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {message && (
                    <div
                        role="status"
                        className={`mb-6 rounded-xl border px-4 py-3 text-sm font-medium ${
                            message.type === 'success'
                                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                                : message.type === 'warning'
                                    ? 'bg-amber-50 border-amber-200 text-amber-800'
                                    : 'bg-red-50 border-red-200 text-red-800'
                        }`}
                    >
                        {message.text}
                    </div>
                )}

                {tab === 'armar' && (
                    <div className="space-y-6">
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div>
                                <div className="text-sm font-semibold text-gray-700">
                                    Stock total de la tienda comparado con el mínimo
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                    {sugerido?.generado_at
                                        ? `Sugerido generado ${fmtDateTime(sugerido.generado_at)}`
                                        : 'Genere el sugerido para ver los productos bajo mínimo.'}
                                </div>
                                <div className="flex flex-wrap gap-3 mt-2 text-xs">
                                    <span className="px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 font-semibold">
                                        {grupos.reduce((acc, grupo) => acc + (grupo.lineas?.length || 0), 0)} productos con oferta
                                    </span>
                                    <span className="px-2 py-1 rounded-full bg-amber-50 text-amber-700 font-semibold">
                                        {sinOfertas.length} sin oferta
                                    </span>
                                    <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold">
                                        {enLista.length} ya en solicitud lista
                                    </span>
                                </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                <button
                                    type="button"
                                    onClick={loadSugerido}
                                    disabled={loadingSugerido}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                                >
                                    <RefreshCw size={16} className={loadingSugerido ? 'animate-spin' : ''} />
                                    Regenerar sugerido
                                </button>
                                {canEdit && grupos.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={guardarBorrador}
                                        disabled={savingDraft || selectedCount === 0}
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                                    >
                                        <Save size={16} />
                                        {savingDraft ? 'Guardando…' : `Guardar borrador (${selectedCount})`}
                                    </button>
                                )}
                            </div>
                        </div>

                        {loadingSugerido && !sugerido && (
                            <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center text-gray-500">
                                Generando sugerido…
                            </div>
                        )}

                        {grupos.length > 0 && (
                            <div className="flex items-center justify-between">
                                <div className="text-xs text-gray-500">
                                    Cada producto queda en el proveedor de menor precio. Los empates se resuelven por preferido y luego por fecha de precio.
                                </div>
                                <button
                                    type="button"
                                    onClick={toggleAll}
                                    className="text-xs font-semibold text-indigo-600 hover:underline"
                                >
                                    {allKeys.length > 0 && allKeys.every((key) => selected[key]) ? 'Quitar selección' : 'Seleccionar todo'}
                                </button>
                            </div>
                        )}

                        {grupos.map((grupo) => {
                            const proveedor = grupo.proveedor || {};
                            const keys = (grupo.lineas || []).map((linea) => lineKey(proveedor.proveedor_id, linea.producto_id));
                            const groupSelected = keys.every((key) => selected[key]);
                            return (
                                <div key={proveedor.proveedor_id} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                                    <div className="px-5 py-4 bg-gray-50/60 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                        <div className="flex items-start gap-3">
                                            <input
                                                type="checkbox"
                                                checked={groupSelected}
                                                onChange={() => toggleGroup(grupo)}
                                                aria-label={`Seleccionar proveedor ${proveedor.nombre}`}
                                                className="mt-1 w-4 h-4 accent-indigo-600"
                                            />
                                            <div>
                                                <div className="font-bold text-gray-800">{proveedor.nombre}</div>
                                                <div className="text-xs text-gray-500">
                                                    {proveedor.telefono || 'Sin teléfono'}
                                                    {proveedor.email ? ` · ${proveedor.email}` : ''}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-xs uppercase font-semibold text-gray-400">Total estimado</div>
                                            <div className="text-lg font-bold text-gray-800">{fmt(grupo.total_estimado)}</div>
                                            <div className="text-[11px] text-gray-400">{grupo.lineas.length} línea(s)</div>
                                        </div>
                                    </div>

                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead className="bg-white border-b border-gray-100">
                                                <tr>
                                                    <th className="py-2 px-3 w-10" />
                                                    <th className="text-left py-2 px-3 text-xs font-bold text-gray-400 uppercase">Producto</th>
                                                    <th className="text-right py-2 px-3 text-xs font-bold text-gray-400 uppercase">Stock / mínimo</th>
                                                    <th className="text-center py-2 px-3 text-xs font-bold text-gray-400 uppercase">Cantidad</th>
                                                    <th className="text-right py-2 px-3 text-xs font-bold text-gray-400 uppercase">Precio</th>
                                                    <th className="text-left py-2 px-3 text-xs font-bold text-gray-400 uppercase">Fecha precio</th>
                                                    <th className="text-right py-2 px-3 text-xs font-bold text-gray-400 uppercase">Total</th>
                                                    <th className="text-left py-2 px-3 text-xs font-bold text-gray-400 uppercase">Otras ofertas</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50">
                                                {(grupo.lineas || []).map((linea) => {
                                                    const key = lineKey(proveedor.proveedor_id, linea.producto_id);
                                                    const cantidad = toNumber(cantidades[key]);
                                                    const totalFila = (Number.isFinite(cantidad) ? cantidad : 0) * Number(linea.precio || 0);
                                                    const otras = linea.otras_ofertas || [];
                                                    const abierta = Boolean(expandedOffers[key]);
                                                    return (
                                                        <Fragment key={key}>
                                                            <tr className={`hover:bg-indigo-50/30 ${selected[key] ? '' : 'opacity-60'}`}>
                                                                <td className="py-3 px-3 align-top">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={Boolean(selected[key])}
                                                                        onChange={(e) => setSelected((prev) => ({ ...prev, [key]: e.target.checked }))}
                                                                        aria-label={`Incluir ${linea.nombre}`}
                                                                        className="mt-1 w-4 h-4 accent-indigo-600"
                                                                    />
                                                                </td>
                                                                <td className="py-3 px-3">
                                                                    <div className="font-semibold text-gray-800">{linea.nombre}</div>
                                                                    <div className="text-xs text-gray-400">
                                                                        {linea.codigo_barra || `ID ${linea.producto_id}`}
                                                                        {linea.codigo_proveedor ? ` · Cód. prov. ${linea.codigo_proveedor}` : ''}
                                                                    </div>
                                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                                        {linea.es_preferido && (
                                                                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">Preferido</span>
                                                                        )}
                                                                        {linea.en_borrador && (
                                                                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">En borrador</span>
                                                                        )}
                                                                        {linea.venta_por_peso && (
                                                                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">Venta por peso</span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td className="py-3 px-3 text-right">
                                                                    <div className="font-medium text-gray-700">
                                                                        {fmtQty(linea.venta_por_peso ? Number(linea.stock_total) / 1000 : linea.stock_total, linea.venta_por_peso)}
                                                                    </div>
                                                                    <div className="text-xs text-gray-400">
                                                                        mín. {fmtQty(linea.venta_por_peso ? Number(linea.stock_minimo) / 1000 : linea.stock_minimo, linea.venta_por_peso)}
                                                                    </div>
                                                                </td>
                                                                <td className="py-3 px-3 text-center">
                                                                    <input
                                                                        type="text"
                                                                        inputMode="decimal"
                                                                        value={cantidades[key] ?? ''}
                                                                        onChange={(e) => updateCantidad(key, e.target.value)}
                                                                        aria-label={`Cantidad a pedir de ${linea.nombre}`}
                                                                        className="w-24 text-center bg-white border border-gray-200 rounded-lg py-1.5 text-sm font-semibold focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none"
                                                                    />
                                                                    <div className="text-[10px] text-gray-400">
                                                                        sugerido {fmtQty(linea.cantidad_sugerida, linea.venta_por_peso)}
                                                                    </div>
                                                                </td>
                                                                <td className="py-3 px-3 text-right">
                                                                    <div className="font-medium text-gray-700">{fmt(linea.precio)}</div>
                                                                </td>
                                                                <td className="py-3 px-3 text-xs text-gray-500">{fmtDate(linea.fecha_precio)}</td>
                                                                <td className="py-3 px-3 text-right font-semibold text-gray-800">{fmt(totalFila)}</td>
                                                                <td className="py-3 px-3">
                                                                    {otras.length > 0 ? (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setExpandedOffers((prev) => ({ ...prev, [key]: !abierta }))}
                                                                            className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:underline"
                                                                        >
                                                                            {abierta ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                                                            {otras.length} más barata(s)
                                                                        </button>
                                                                    ) : (
                                                                        <span className="text-xs text-gray-300">—</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                            {abierta && otras.length > 0 && (
                                                                <tr className="bg-indigo-50/30">
                                                                    <td />
                                                                    <td colSpan={7} className="px-3 py-2">
                                                                        <div className="text-[11px] uppercase font-bold text-gray-400 mb-1">Referencia interna · no se imprime</div>
                                                                        <div className="flex flex-wrap gap-2">
                                                                            {otras.map((oferta) => (
                                                                                <div key={`${oferta.proveedor_id}-${oferta.precio}`} className="bg-white border border-indigo-100 rounded-lg px-3 py-2 text-xs">
                                                                                    <div className="font-semibold text-gray-700">{oferta.proveedor_nombre}</div>
                                                                                    <div className="text-gray-500">
                                                                                        {fmt(oferta.precio)} · {fmtDate(oferta.fecha_precio)}
                                                                                        {oferta.es_preferido ? ' · preferido' : ''}
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </Fragment>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            );
                        })}

                        {canEdit && grupos.length === 0 && sugerido && (
                            <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center">
                                <CheckCircle2 className="mx-auto text-emerald-500 mb-3" size={32} />
                                <p className="font-semibold text-gray-700">No hay productos bajo mínimo con oferta</p>
                                <p className="text-sm text-gray-500 mt-1">Cuando un producto llegue a su stock mínimo y tenga una oferta con precio, aparecerá aquí.</p>
                            </div>
                        )}

                        {sinOfertas.length > 0 && (
                            <div className="bg-white rounded-2xl shadow-sm border border-amber-200 overflow-hidden">
                                <div className="px-5 py-4 bg-amber-50/70 border-b border-amber-100 flex items-center gap-2">
                                    <AlertTriangle className="text-amber-600" size={18} />
                                    <div>
                                        <h2 className="font-bold text-amber-900">Productos bajo mínimo sin oferta</h2>
                                        <p className="text-xs text-amber-700">No entran en una solicitud hasta cargarles al menos un proveedor con precio.</p>
                                    </div>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead className="bg-white border-b border-gray-100">
                                            <tr>
                                                <th className="text-left py-2 px-4 text-xs font-bold text-gray-400 uppercase">Producto</th>
                                                <th className="text-right py-2 px-4 text-xs font-bold text-gray-400 uppercase">Stock / mínimo</th>
                                                <th className="text-right py-2 px-4 text-xs font-bold text-gray-400 uppercase">Sugerido</th>
                                                <th className="text-right py-2 px-4 text-xs font-bold text-gray-400 uppercase">Acción</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                            {sinOfertas.map((row) => (
                                                <tr key={row.producto_id} className="hover:bg-amber-50/30">
                                                    <td className="py-3 px-4">
                                                        <div className="font-semibold text-gray-800">{row.nombre}</div>
                                                        <div className="text-xs text-gray-400">{row.codigo_barra || `ID ${row.producto_id}`}</div>
                                                        {row.tiene_oferta_sin_precio && (
                                                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                                                                Tiene proveedor sin precio
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="py-3 px-4 text-right text-gray-600">
                                                        {fmtQty(row.stock_total)} / {fmtQty(row.stock_minimo)}
                                                    </td>
                                                    <td className="py-3 px-4 text-right font-medium">{fmtQty(row.cantidad_sugerida)}</td>
                                                    <td className="py-3 px-4 text-right">
                                                        <button
                                                            type="button"
                                                            onClick={() => abrirOfertas({ id: row.producto_id, nombre: row.nombre, codigo_barra: row.codigo_barra })}
                                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 text-xs font-semibold text-white hover:bg-amber-700"
                                                        >
                                                            <Plus size={14} />
                                                            Cargar oferta
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {enLista.length > 0 && (
                            <div className="bg-white rounded-2xl shadow-sm border border-emerald-200 overflow-hidden">
                                <div className="px-5 py-4 bg-emerald-50/70 border-b border-emerald-100 flex items-center gap-2">
                                    <CheckCircle2 className="text-emerald-600" size={18} />
                                    <div>
                                        <h2 className="font-bold text-emerald-900">Ya están en una solicitud lista</h2>
                                        <p className="text-xs text-emerald-700">No se vuelven a pedir hasta recepcionar o anular esa solicitud.</p>
                                    </div>
                                </div>
                                <ul className="divide-y divide-gray-50">
                                    {enLista.map((row) => (
                                        <li key={row.producto_id} className="px-5 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-1 text-sm">
                                            <span className="font-medium text-gray-700">{row.nombre}</span>
                                            <span className="text-xs text-gray-500">
                                                {row.proveedor_nombre} · sugerido {fmtQty(row.cantidad_sugerida)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                {tab === 'solicitudes' && (
                    <div className="space-y-4">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="bg-white p-1 rounded-lg shadow-sm border border-gray-200 inline-flex flex-wrap">
                                {ESTADOS.map((estado) => (
                                    <button
                                        key={estado.value}
                                        type="button"
                                        onClick={() => setEstadoFiltro(estado.value)}
                                        className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                                            estadoFiltro === estado.value
                                                ? 'bg-indigo-500 text-white shadow-sm'
                                                : 'text-gray-600 hover:bg-gray-50'
                                        }`}
                                    >
                                        {estado.label}
                                    </button>
                                ))}
                            </div>
                            <button
                                type="button"
                                onClick={() => loadSolicitudes(estadoFiltro)}
                                disabled={loadingSolicitudes}
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                            >
                                <RefreshCw size={14} className={loadingSolicitudes ? 'animate-spin' : ''} />
                                Actualizar
                            </button>
                        </div>

                        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                            {loadingSolicitudes ? (
                                <div className="p-12 text-center text-gray-500">Cargando solicitudes…</div>
                            ) : solicitudes.length === 0 ? (
                                <div className="p-12 text-center">
                                    <FileText className="mx-auto text-gray-300 mb-3" size={32} />
                                    <p className="font-medium text-gray-600">No hay solicitudes en este filtro</p>
                                    <p className="text-sm text-gray-400 mt-1">Armá un pedido desde el sugerido para crear el primer borrador.</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-50 border-b border-gray-100">
                                            <tr>
                                                <th className="text-left py-3 px-4 text-xs font-bold text-gray-400 uppercase">Solicitud</th>
                                                <th className="text-left py-3 px-4 text-xs font-bold text-gray-400 uppercase">Proveedor</th>
                                                <th className="text-left py-3 px-4 text-xs font-bold text-gray-400 uppercase">Estado</th>
                                                <th className="text-right py-3 px-4 text-xs font-bold text-gray-400 uppercase">Líneas</th>
                                                <th className="text-right py-3 px-4 text-xs font-bold text-gray-400 uppercase">Total estimado</th>
                                                <th className="text-left py-3 px-4 text-xs font-bold text-gray-400 uppercase">Fechas</th>
                                                <th className="text-right py-3 px-4 text-xs font-bold text-gray-400 uppercase">Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                            {solicitudes.map((sol) => (
                                                <tr key={sol.id} className="hover:bg-indigo-50/30">
                                                    <td className="py-3 px-4">
                                                        <div className="font-bold text-gray-800">{sol.numero}</div>
                                                        <div className="text-xs text-gray-400">
                                                            {sol.estado === 'recibida' && sol.compra_id ? `Compra #${sol.compra_id}` : `Creada por ${sol.usuario_nombre || '—'}`}
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        <div className="font-medium text-gray-700">{sol.proveedor_nombre}</div>
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        <span className={`text-[11px] font-bold px-2 py-1 rounded-full ${ESTADO_CLASS[sol.estado] || 'bg-gray-100 text-gray-700'}`}>
                                                            {ESTADO_LABEL[sol.estado] || sol.estado}
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-4 text-right text-gray-600">{sol.lineas}</td>
                                                    <td className="py-3 px-4 text-right font-semibold text-gray-800">{fmt(sol.total_estimado)}</td>
                                                    <td className="py-3 px-4 text-xs text-gray-500">
                                                        <div>Creada {fmtDate(sol.fecha_solicitud || sol.created_at)}</div>
                                                        {sol.fecha_lista && <div>Lista {fmtDate(sol.fecha_lista)}</div>}
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        <div className="flex flex-wrap items-center justify-end gap-1.5">
                                                            <button
                                                                type="button"
                                                                onClick={() => abrirSolicitud(sol.id)}
                                                                title="Ver detalle"
                                                                aria-label={`Ver ${sol.numero}`}
                                                                className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                                                            >
                                                                <FileText size={15} />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => imprimirSolicitud(sol.id)}
                                                                title="Imprimir"
                                                                aria-label={`Imprimir ${sol.numero}`}
                                                                className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                                                            >
                                                                <Printer size={15} />
                                                            </button>
                                                            {canEdit && sol.estado === 'borrador' && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => abrirSolicitud(sol.id)}
                                                                    title="Editar borrador"
                                                                    aria-label={`Editar ${sol.numero}`}
                                                                    className="p-1.5 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                                                                >
                                                                    <ClipboardList size={15} />
                                                                </button>
                                                            )}
                                                            {canEdit && sol.estado === 'lista' && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => abrirRecepcion(sol.id)}
                                                                    title="Recepcionar"
                                                                    aria-label={`Recepcionar ${sol.numero}`}
                                                                    className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg bg-emerald-600 text-xs font-semibold text-white hover:bg-emerald-700"
                                                                >
                                                                    <Truck size={14} />
                                                                    Recibir
                                                                </button>
                                                            )}
                                                            {canEdit && ['borrador', 'lista'].includes(sol.estado) && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => anularSolicitud(sol.id)}
                                                                    title="Anular"
                                                                    aria-label={`Anular ${sol.numero}`}
                                                                    className="p-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                                                                >
                                                                    <XCircle size={15} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {tab === 'ofertas' && (
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                        <div className="space-y-6">
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
                                <h2 className="font-bold text-gray-800 flex items-center gap-2">
                                    <Search size={18} className="text-indigo-500" />
                                    Buscar producto
                                </h2>
                                <p className="text-xs text-gray-500 mt-1">Busque por nombre o código de barra para cargar sus ofertas.</p>
                                <input
                                    type="text"
                                    value={productSearch}
                                    onChange={(e) => setProductSearch(e.target.value)}
                                    placeholder="Ej: arroz o 780123456789"
                                    className="mt-3 w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none"
                                />
                                <div className="mt-3 space-y-1.5 max-h-64 overflow-y-auto">
                                    {searchingProducts && <div className="text-xs text-gray-400 py-2">Buscando…</div>}
                                    {!searchingProducts && productSearch.trim().length >= 2 && productResults.length === 0 && (
                                        <div className="text-xs text-gray-400 py-2">Sin resultados.</div>
                                    )}
                                    {productResults.map((product) => (
                                        <button
                                            key={product.id}
                                            type="button"
                                            onClick={() => abrirOfertas(product)}
                                            className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-all ${
                                                String(offerProduct?.id) === String(product.id)
                                                    ? 'border-indigo-300 bg-indigo-50'
                                                    : 'border-gray-100 hover:bg-gray-50'
                                            }`}
                                        >
                                            <div className="font-medium text-gray-700">{product.nombre}</div>
                                            <div className="text-xs text-gray-400 flex justify-between gap-2">
                                                <span>{product.codigo_barra || `ID ${product.id}`}</span>
                                                <span>{product.ofertas} oferta(s)</span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="bg-white rounded-2xl shadow-sm border border-amber-200 overflow-hidden">
                                <div className="px-5 py-4 bg-amber-50/70 border-b border-amber-100">
                                    <h2 className="font-bold text-amber-900">Bajo mínimo sin oferta</h2>
                                    <p className="text-xs text-amber-700 mt-1">{pendientes.total} producto(s) esperando proveedor.</p>
                                </div>
                                <div className="p-3 border-b border-gray-100">
                                    <input
                                        type="text"
                                        value={pendientesSearch}
                                        onChange={(e) => setPendientesSearch(e.target.value)}
                                        placeholder="Filtrar pendientes…"
                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs focus:bg-white focus:ring-2 focus:ring-amber-100 outline-none"
                                    />
                                </div>
                                <div className="max-h-80 overflow-y-auto divide-y divide-gray-50">
                                    {pendientes.productos.length === 0 ? (
                                        <div className="p-4 text-xs text-gray-400 text-center">Sin pendientes.</div>
                                    ) : pendientes.productos.map((row) => (
                                        <button
                                            key={row.producto_id}
                                            type="button"
                                            onClick={() => abrirOfertas({ id: row.producto_id, nombre: row.nombre, codigo_barra: row.codigo_barra })}
                                            className="w-full text-left px-4 py-2.5 text-sm hover:bg-amber-50/40"
                                        >
                                            <div className="font-medium text-gray-700">{row.nombre}</div>
                                            <div className="text-[11px] text-gray-400">
                                                stock {fmtQty(row.stock_total)} · mín. {fmtQty(row.stock_minimo)}
                                                {row.tiene_oferta_sin_precio ? ' · proveedor sin precio' : ''}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="xl:col-span-2 space-y-6">
                            {!offerProduct ? (
                                <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
                                    <Tag className="mx-auto text-gray-300 mb-3" size={32} />
                                    <p className="font-semibold text-gray-700">Seleccione un producto</p>
                                    <p className="text-sm text-gray-500 mt-1">Busque un producto o elija uno de la lista de pendientes para cargar sus proveedores y precios.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                            <div>
                                                <h2 className="text-lg font-bold text-gray-800">{offerProduct.nombre}</h2>
                                                <div className="text-xs text-gray-500">
                                                    {offerProduct.codigo_barra || `ID ${offerProduct.id}`}
                                                    {offerProduct.stock_minimo != null ? ` · mínimo ${fmtQty(offerProduct.stock_minimo)}` : ''}
                                                </div>
                                            </div>
                                            <span className="text-xs font-semibold px-2 py-1 rounded-full bg-indigo-50 text-indigo-700">
                                                {offers.length} proveedor(es)
                                            </span>
                                        </div>
                                    </div>

                                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                                        <div className="px-5 py-4 border-b border-gray-100">
                                            <h3 className="font-bold text-gray-800">Ofertas registradas</h3>
                                            <p className="text-xs text-gray-500 mt-1">El sugerido usa el menor precio. El preferido solo desempata.</p>
                                        </div>
                                        {loadingOffers ? (
                                            <div className="p-8 text-center text-gray-500 text-sm">Cargando ofertas…</div>
                                        ) : offers.length === 0 ? (
                                            <div className="p-8 text-center text-sm text-gray-500">Este producto todavía no tiene ofertas.</div>
                                        ) : (
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-sm">
                                                    <thead className="bg-gray-50 border-b border-gray-100">
                                                        <tr>
                                                            <th className="text-left py-2 px-4 text-xs font-bold text-gray-400 uppercase">Proveedor</th>
                                                            <th className="text-left py-2 px-4 text-xs font-bold text-gray-400 uppercase">Código</th>
                                                            <th className="text-right py-2 px-4 text-xs font-bold text-gray-400 uppercase">Precio</th>
                                                            <th className="text-left py-2 px-4 text-xs font-bold text-gray-400 uppercase">Fecha precio</th>
                                                            <th className="text-center py-2 px-4 text-xs font-bold text-gray-400 uppercase">Preferido</th>
                                                            <th className="text-right py-2 px-4 text-xs font-bold text-gray-400 uppercase">Acciones</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-50">
                                                        {offers.map((oferta) => (
                                                            <tr key={oferta.id} className={oferta.precio <= 0 ? 'bg-amber-50/40' : 'hover:bg-indigo-50/30'}>
                                                                <td className="py-3 px-4 font-medium text-gray-700">{oferta.proveedor_nombre}</td>
                                                                <td className="py-3 px-4 text-gray-500">{oferta.codigo_proveedor || '—'}</td>
                                                                <td className="py-3 px-4 text-right font-semibold text-gray-800">
                                                                    {oferta.precio > 0 ? fmt(oferta.precio) : <span className="text-amber-700 text-xs">Sin precio</span>}
                                                                </td>
                                                                <td className="py-3 px-4 text-xs text-gray-500">{fmtDate(oferta.fecha_precio)}</td>
                                                                <td className="py-3 px-4 text-center">
                                                                    {oferta.es_preferido ? (
                                                                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">Sí</span>
                                                                    ) : (
                                                                        <span className="text-xs text-gray-300">—</span>
                                                                    )}
                                                                </td>
                                                                <td className="py-3 px-4">
                                                                    {canEdit && (
                                                                        <div className="flex justify-end gap-1.5">
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => editarOferta(oferta)}
                                                                                className="px-2 py-1 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                                                                            >
                                                                                Editar
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => eliminarOferta(oferta)}
                                                                                aria-label={`Eliminar oferta de ${oferta.proveedor_nombre}`}
                                                                                className="p-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                                                                            >
                                                                                <Trash2 size={14} />
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>

                                    {canEdit && (
                                        <form onSubmit={guardarOferta} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
                                            <h3 className="font-bold text-gray-800">Agregar o actualizar oferta</h3>
                                            <p className="text-xs text-gray-500 mt-1">Guardar una cotización no crea una compra ni mueve stock.</p>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                                    Proveedor
                                                    <select
                                                        value={offerForm.proveedor_id}
                                                        onChange={(e) => setOfferForm((prev) => ({ ...prev, proveedor_id: e.target.value }))}
                                                        className="mt-1 w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 normal-case tracking-normal focus:bg-white focus:ring-2 focus:ring-indigo-100 outline-none"
                                                    >
                                                        <option value="">Seleccionar proveedor…</option>
                                                        {proveedores.map((prov) => (
                                                            <option key={prov.proveedor_id} value={prov.proveedor_id}>{prov.nombre}</option>
                                                        ))}
                                                    </select>
                                                </label>
                                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                                    Precio de compra
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={offerForm.precio}
                                                        onChange={(e) => setOfferForm((prev) => ({ ...prev, precio: e.target.value }))}
                                                        placeholder="Ej: 1290"
                                                        className="mt-1 w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium focus:bg-white focus:ring-2 focus:ring-indigo-100 outline-none"
                                                    />
                                                </label>
                                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                                    Código del proveedor
                                                    <input
                                                        type="text"
                                                        value={offerForm.codigo_proveedor}
                                                        onChange={(e) => setOfferForm((prev) => ({ ...prev, codigo_proveedor: e.target.value }))}
                                                        placeholder="Opcional"
                                                        className="mt-1 w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium focus:bg-white focus:ring-2 focus:ring-indigo-100 outline-none"
                                                    />
                                                </label>
                                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                                    Fecha del precio
                                                    <input
                                                        type="date"
                                                        value={offerForm.fecha_precio}
                                                        onChange={(e) => setOfferForm((prev) => ({ ...prev, fecha_precio: e.target.value }))}
                                                        className="mt-1 w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium focus:bg-white focus:ring-2 focus:ring-indigo-100 outline-none"
                                                    />
                                                </label>
                                            </div>
                                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-5">
                                                <label className="inline-flex items-center gap-2 text-sm text-gray-600">
                                                    <input
                                                        type="checkbox"
                                                        checked={offerForm.es_preferido}
                                                        onChange={(e) => setOfferForm((prev) => ({ ...prev, es_preferido: e.target.checked }))}
                                                        className="w-4 h-4 accent-indigo-600"
                                                    />
                                                    Marcar como preferido (solo desempata)
                                                </label>
                                                <button
                                                    type="submit"
                                                    disabled={savingOffer}
                                                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                                                >
                                                    <Save size={16} />
                                                    {savingOffer ? 'Guardando…' : 'Guardar oferta'}
                                                </button>
                                            </div>
                                        </form>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Detalle de solicitud */}
            {(detail || loadingDetail) && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl my-6">
                        {loadingDetail && !detail ? (
                            <div className="p-12 text-center text-gray-500">Cargando solicitud…</div>
                        ) : detail && (
                            <>
                                <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
                                    <div>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <h2 className="text-xl font-bold text-gray-800">{detail.numero}</h2>
                                            <span className={`text-[11px] font-bold px-2 py-1 rounded-full ${ESTADO_CLASS[detail.estado] || 'bg-gray-100'}`}>
                                                {ESTADO_LABEL[detail.estado] || detail.estado}
                                            </span>
                                        </div>
                                        <div className="text-sm text-gray-500 mt-1">
                                            {detail.proveedor_nombre}
                                            {detail.proveedor_telefono ? ` · ${detail.proveedor_telefono}` : ''}
                                            {detail.proveedor_email ? ` · ${detail.proveedor_email}` : ''}
                                        </div>
                                        <div className="text-xs text-gray-400 mt-1">
                                            Creada {fmtDateTime(detail.fecha_solicitud || detail.created_at)}
                                            {detail.fecha_lista ? ` · Lista ${fmtDateTime(detail.fecha_lista)}` : ''}
                                            {detail.compra_id ? ` · Compra #${detail.compra_id}` : ''}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setDetail(null)}
                                        aria-label="Cerrar detalle"
                                        className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                                    >
                                        <X size={20} />
                                    </button>
                                </div>

                                <div className="p-5 space-y-4">
                                    <div className="overflow-x-auto border border-gray-100 rounded-xl">
                                        <table className="w-full text-sm">
                                            <thead className="bg-gray-50 border-b border-gray-100">
                                                <tr>
                                                    <th className="text-left py-2 px-3 text-xs font-bold text-gray-400 uppercase">Producto</th>
                                                    <th className="text-right py-2 px-3 text-xs font-bold text-gray-400 uppercase">Stock / mínimo</th>
                                                    <th className="text-center py-2 px-3 text-xs font-bold text-gray-400 uppercase">Sugerido</th>
                                                    <th className="text-center py-2 px-3 text-xs font-bold text-gray-400 uppercase">Pedido</th>
                                                    <th className="text-right py-2 px-3 text-xs font-bold text-gray-400 uppercase">Precio</th>
                                                    <th className="text-right py-2 px-3 text-xs font-bold text-gray-400 uppercase">Total</th>
                                                    {detail.estado === 'borrador' && canEdit && <th className="w-10" />}
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50">
                                                {detail.lineas.map((linea) => (
                                                    <Fragment key={linea.id}>
                                                        <tr className={linea._removed ? 'bg-red-50/60 line-through text-gray-400' : ''}>
                                                            <td className="py-3 px-3">
                                                                <div className="font-medium text-gray-800">{linea.producto_nombre}</div>
                                                                <div className="text-xs text-gray-400">
                                                                    {linea.codigo_proveedor ? `Cód. prov. ${linea.codigo_proveedor} · ` : ''}
                                                                    {linea.codigo_barra || `ID ${linea.producto_id}`}
                                                                </div>
                                                            </td>
                                                            <td className="py-3 px-3 text-right text-gray-600">
                                                                {fmtQty(linea.venta_por_peso ? Number(linea.stock_total) / 1000 : linea.stock_total, linea.venta_por_peso)}
                                                                <div className="text-xs text-gray-400">
                                                                    mín. {fmtQty(linea.venta_por_peso ? Number(linea.stock_minimo) / 1000 : linea.stock_minimo, linea.venta_por_peso)}
                                                                </div>
                                                            </td>
                                                            <td className="py-3 px-3 text-center text-gray-500">{fmtQty(linea.cantidad_sugerida, linea.venta_por_peso)}</td>
                                                            <td className="py-3 px-3 text-center">
                                                                {detail.estado === 'borrador' && canEdit && !linea._removed ? (
                                                                    <input
                                                                        type="text"
                                                                        inputMode="decimal"
                                                                        value={linea.cantidad_pedida}
                                                                        onChange={(e) => actualizarLineaDetalle(linea.id, 'cantidad_pedida', e.target.value)}
                                                                        aria-label={`Cantidad pedida de ${linea.producto_nombre}`}
                                                                        className="w-24 text-center bg-white border border-gray-200 rounded-lg py-1.5 text-sm font-semibold focus:ring-2 focus:ring-indigo-100 outline-none"
                                                                    />
                                                                ) : (
                                                                    <span className="font-semibold text-gray-700">{fmtQty(linea.cantidad_pedida, linea.venta_por_peso)}</span>
                                                                )}
                                                            </td>
                                                            <td className="py-3 px-3 text-right text-gray-700">{fmt(linea.precio_unitario)}</td>
                                                            <td className="py-3 px-3 text-right font-semibold text-gray-800">{fmt(totalLinea(linea))}</td>
                                                            {detail.estado === 'borrador' && canEdit && (
                                                                <td className="py-3 px-3 text-right">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => eliminarLineaDetalle(linea.id)}
                                                                        aria-label={linea._removed ? `Restaurar ${linea.producto_nombre}` : `Quitar ${linea.producto_nombre}`}
                                                                        className={`p-1.5 rounded-lg border ${linea._removed ? 'border-emerald-200 text-emerald-600 hover:bg-emerald-50' : 'border-red-200 text-red-600 hover:bg-red-50'}`}
                                                                    >
                                                                        {linea._removed ? <Plus size={14} /> : <Trash2 size={14} />}
                                                                    </button>
                                                                </td>
                                                            )}
                                                        </tr>
                                                        {linea.otras_ofertas?.length > 0 && (
                                                            <tr className="bg-indigo-50/20">
                                                                <td colSpan={detail.estado === 'borrador' && canEdit ? 7 : 6} className="px-3 py-2">
                                                                    <span className="text-[10px] uppercase font-bold text-gray-400 mr-2">Otras ofertas (solo referencia)</span>
                                                                    <span className="text-xs text-gray-500">
                                                                        {linea.otras_ofertas
                                                                            .map((oferta) => `${oferta.proveedor_nombre}: ${fmt(oferta.precio)} (${fmtDate(oferta.fecha_precio)})`)
                                                                            .join(' · ')}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </Fragment>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                                        <label className="md:col-span-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                            Observaciones
                                            <textarea
                                                value={detail.observaciones || ''}
                                                onChange={(e) => setDetail((prev) => ({ ...prev, observaciones: e.target.value }))}
                                                disabled={detail.estado !== 'borrador' || !canEdit}
                                                rows={2}
                                                className="mt-1 w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm font-normal normal-case tracking-normal focus:bg-white focus:ring-2 focus:ring-indigo-100 outline-none disabled:opacity-70"
                                                placeholder="Ej: entregar antes del viernes"
                                            />
                                        </label>
                                        <div className="bg-gray-50 rounded-xl border border-gray-100 p-3 text-right">
                                            <div className="text-xs uppercase font-semibold text-gray-400">Total estimado</div>
                                            <div className="text-xl font-bold text-gray-800">{fmt(detail.total_estimado)}</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="px-5 py-4 border-t border-gray-100 flex flex-wrap items-center justify-end gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setDetail(null)}
                                        className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50"
                                    >
                                        Cerrar
                                    </button>
                                    <button
                                        type="button"
                                        onClick={imprimirDetalle}
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                                    >
                                        <Printer size={16} />
                                        Imprimir
                                    </button>
                                    {canEdit && detail.estado === 'borrador' && (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() => guardarDetalle()}
                                                disabled={savingDetail}
                                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-indigo-200 bg-indigo-50 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-60"
                                            >
                                                <Save size={16} />
                                                {savingDetail ? 'Guardando…' : 'Guardar cambios'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={dejarLista}
                                                disabled={savingDetail}
                                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                                            >
                                                <CheckCircle2 size={16} />
                                                Dejar lista
                                            </button>
                                        </>
                                    )}
                                    {canEdit && detail.estado === 'lista' && (
                                        <button
                                            type="button"
                                            onClick={() => abrirRecepcion(detail.id)}
                                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-sm font-semibold text-white hover:bg-emerald-700"
                                        >
                                            <Truck size={16} />
                                            Recepcionar
                                        </button>
                                    )}
                                    {canEdit && ['borrador', 'lista'].includes(detail.estado) && (
                                        <button
                                            type="button"
                                            onClick={() => anularSolicitud(detail.id)}
                                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 text-sm font-semibold text-red-600 hover:bg-red-50"
                                        >
                                            <XCircle size={16} />
                                            Anular
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Recepción */}
            {reception && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl my-6">
                        <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
                            <div>
                                <h2 className="text-xl font-bold text-gray-800">Recepcionar {reception.numero}</h2>
                                <div className="text-sm text-gray-500 mt-1">{reception.proveedor_nombre}</div>
                                <p className="text-xs text-gray-400 mt-1">
                                    El stock entra por la cantidad recibida. Los precios se actualizan con la lógica de compras y pueden quedar pendientes de autorización.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setReception(null)}
                                aria-label="Cerrar recepción"
                                className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-5 space-y-5">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                    Bodega de ingreso
                                    <select
                                        value={receptionForm.bodega_id}
                                        onChange={(e) => setReceptionForm((prev) => ({ ...prev, bodega_id: e.target.value }))}
                                        className="mt-1 w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 normal-case tracking-normal focus:bg-white focus:ring-2 focus:ring-indigo-100 outline-none"
                                    >
                                        <option value="">Seleccionar bodega…</option>
                                        {bodegas.map((bodega) => (
                                            <option key={bodega.bodega_id} value={bodega.bodega_id}>
                                                {bodega.sucursal_nombre ? `${bodega.sucursal_nombre} · ` : ''}{bodega.nombre}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                    N° factura
                                    <input
                                        type="text"
                                        value={receptionForm.numero_factura}
                                        onChange={(e) => setReceptionForm((prev) => ({ ...prev, numero_factura: e.target.value }))}
                                        placeholder="Opcional"
                                        className="mt-1 w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium normal-case tracking-normal focus:bg-white focus:ring-2 focus:ring-indigo-100 outline-none"
                                    />
                                </label>
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                    Fecha compra
                                    <input
                                        type="date"
                                        value={receptionForm.fecha_compra}
                                        onChange={(e) => setReceptionForm((prev) => ({ ...prev, fecha_compra: e.target.value }))}
                                        className="mt-1 w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium normal-case tracking-normal focus:bg-white focus:ring-2 focus:ring-indigo-100 outline-none"
                                    />
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                        ILA
                                        <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            value={receptionForm.ila}
                                            onChange={(e) => setReceptionForm((prev) => ({ ...prev, ila: e.target.value }))}
                                            placeholder="0"
                                            className="mt-1 w-full px-2 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium normal-case tracking-normal focus:bg-white focus:ring-2 focus:ring-indigo-100 outline-none"
                                        />
                                    </label>
                                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                        Flete
                                        <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            value={receptionForm.flete}
                                            onChange={(e) => setReceptionForm((prev) => ({ ...prev, flete: e.target.value }))}
                                            placeholder="0"
                                            className="mt-1 w-full px-2 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium normal-case tracking-normal focus:bg-white focus:ring-2 focus:ring-indigo-100 outline-none"
                                        />
                                    </label>
                                </div>
                            </div>

                            <div className="overflow-x-auto border border-gray-100 rounded-xl">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 border-b border-gray-100">
                                        <tr>
                                            <th className="text-left py-2 px-3 text-xs font-bold text-gray-400 uppercase">Producto</th>
                                            <th className="text-center py-2 px-3 text-xs font-bold text-gray-400 uppercase">Pedido</th>
                                            <th className="text-center py-2 px-3 text-xs font-bold text-gray-400 uppercase">Recibido</th>
                                            <th className="text-right py-2 px-3 text-xs font-bold text-gray-400 uppercase">Precio factura</th>
                                            <th className="text-center py-2 px-3 text-xs font-bold text-gray-400 uppercase">Vencimiento</th>
                                            <th className="text-right py-2 px-3 text-xs font-bold text-gray-400 uppercase">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {receptionLineas.map((linea) => (
                                            <tr key={linea.id}>
                                                <td className="py-3 px-3">
                                                    <div className="font-medium text-gray-800">{linea.producto_nombre}</div>
                                                    <div className="text-xs text-gray-400">
                                                        {linea.codigo_barra || `ID ${linea.producto_id}`}
                                                        {linea.venta_por_peso ? ' · kg' : ''}
                                                    </div>
                                                </td>
                                                <td className="py-3 px-3 text-center text-gray-500">{fmtQty(linea.cantidad_pedida, linea.venta_por_peso)}</td>
                                                <td className="py-3 px-3 text-center">
                                                    <input
                                                        type="text"
                                                        inputMode="decimal"
                                                        value={linea.cantidad_recibida}
                                                        onChange={(e) => actualizarRecepcionLinea(linea.id, 'cantidad_recibida', e.target.value)}
                                                        aria-label={`Cantidad recibida de ${linea.producto_nombre}`}
                                                        className="w-24 text-center bg-white border border-gray-200 rounded-lg py-1.5 text-sm font-semibold focus:ring-2 focus:ring-indigo-100 outline-none"
                                                    />
                                                </td>
                                                <td className="py-3 px-3 text-right">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={linea.precio_unitario}
                                                        onChange={(e) => actualizarRecepcionLinea(linea.id, 'precio_unitario', e.target.value)}
                                                        aria-label={`Precio de factura de ${linea.producto_nombre}`}
                                                        className="w-28 text-right bg-white border border-gray-200 rounded-lg py-1.5 text-sm focus:ring-2 focus:ring-indigo-100 outline-none"
                                                    />
                                                </td>
                                                <td className="py-3 px-3">
                                                    <div className="flex flex-col gap-1">
                                                        <label className="flex items-center justify-center gap-1.5 text-[11px] text-gray-600">
                                                            <input
                                                                type="checkbox"
                                                                checked={Boolean(linea.requiere_expiracion)}
                                                                onChange={(e) => actualizarRecepcionLinea(linea.id, 'requiere_expiracion', e.target.checked)}
                                                                className="w-3.5 h-3.5 accent-indigo-600"
                                                            />
                                                            Requiere
                                                        </label>
                                                        {linea.requiere_expiracion && (
                                                            <input
                                                                type="date"
                                                                value={linea.fecha_expiracion || ''}
                                                                onChange={(e) => actualizarRecepcionLinea(linea.id, 'fecha_expiracion', e.target.value)}
                                                                className="bg-white border border-gray-200 rounded-lg px-2 py-1 text-xs focus:ring-2 focus:ring-indigo-100 outline-none"
                                                            />
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="py-3 px-3 text-right font-semibold text-gray-800">
                                                    {fmt(Math.round((Number(toNumber(linea.cantidad_recibida)) || 0) * (Number(toNumber(linea.precio_unitario)) || 0)))}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-gray-50 rounded-xl border border-gray-100 p-4">
                                <div className="text-xs text-gray-500">
                                    Se registrará una compra por <span className="font-semibold text-gray-700">{reception.proveedor_nombre}</span> y el stock entrará en la bodega elegida.
                                </div>
                                <div className="text-right">
                                    <div className="text-xs uppercase font-semibold text-gray-400">Total a registrar</div>
                                    <div className="text-xl font-bold text-gray-800">{fmt(recepcionTotal)}</div>
                                </div>
                            </div>
                        </div>

                        <div className="px-5 py-4 border-t border-gray-100 flex flex-wrap items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setReception(null)}
                                className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={recepcionar}
                                disabled={receiving}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                            >
                                <Truck size={16} />
                                {receiving ? 'Recepcionando…' : 'Recepcionar y sumar stock'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
